import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { useForm, useWatch } from "react-hook-form";

import type {
  ConstraintListItemDto,
  TicketCategoryDetailsDto,
  TicketCategoryDto,
  UserDto,
  UserRoleRepairCategoryPermissionsDto,
} from "@/api/types";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { FormRadioCard, FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormSwitch } from "@/components/Form/FormSwitch";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { Wizard, WizardStep } from "@/components/Wizard/Wizard";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import type { FormPermissions } from "@/helpers/permissions";
import { createRequiredStringRule } from "@/helpers/rules";
import { useSlug } from "@/hooks/useSlug";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { TicketCategoryTree } from "./TicketCategoryTree";

export interface TicketCategoryFormProps {
  ticketCategory: TicketCategoryDetailsDto | undefined;
  isEditMode: boolean;
  defaultFormValues: TicketCategoryDetailsFormValues;
  isSubmitting: boolean;
  onSubmit: (data: TicketCategoryDetailsFormValues) => void;
}

const TABS_ID = {
  STEP1: 1,
  STEP2: 2,
  STEP3: 3,
};
export const DAYS_VARIANTS = ["14", "30", "60", "90", "120"] as const;
const NAME_MAX_LENGTH = 30;

export interface TicketCategoryDetailsFormValues {
  type: "single" | "tree";
  name: string;
  notificationType: TicketCategoryDto["notificationType"];
  closeAfterInactiveDayAmount: `${number}`;
  enabled: boolean;
  defaultAssignee?: UserDto;
  audience: ConstraintListItemDto[];
  permissions: FormPermissions<UserRoleRepairCategoryPermissionsDto>;
  reopenPolicy: TicketCategoryDetailsDto["reopenPolicy"];
  treeVveEnabled: boolean;
  treeOverrideExternalCode: string;
  requirePhoneNumber: "mandatory" | "notRequired";
  dicoEnabled?: boolean;
  requireImages?: "mandatory" | "notRequired";
}

export function TicketCategoryForm(props: TicketCategoryFormProps): React.ReactNode {
  const { isEditMode, defaultFormValues } = props;
  const { t } = useTranslation();
  const slug = useSlug();
  const formMethods = useForm<TicketCategoryDetailsFormValues>({
    mode: "onChange",
    defaultValues: defaultFormValues,
  });

  const closeAfterInactiveDayAmount = useWatch({
    control: formMethods.control,
    name: "closeAfterInactiveDayAmount",
  });
  const audience = useWatch({
    control: formMethods.control,
    name: "audience",
  });
  const permissions = useWatch({
    control: formMethods.control,
    name: "permissions",
  });
  const treeOverrideExternalCode = useWatch({
    control: formMethods.control,
    name: "treeOverrideExternalCode",
  });
  const treeVveEnabled = useWatch({
    control: formMethods.control,
    name: "treeVveEnabled",
  });

  const ticketCategoryQueries = useTicketCategoryQueries();
  const { data: possibleAssignees = [] } = useQuery(ticketCategoryQueries.listAssignees());

  const commonWizardActionValidation = async () => {
    return await formMethods.trigger();
  };

  const permissionFields = [
    { key: "canAddRequest", label: t("model.permission.repair-category.can-add-request") },
    { key: "canCommentInternal", label: t("model.permission.repair-category.can-comment-internal") },
    { key: "canCommentPublic", label: t("model.permission.repair-category.can-comment-public") },
    { key: "canListAllRequests", label: t("model.permission.repair-category.can-list-all") },
    { key: "canListAssignedToMeRequests", label: t("model.permission.repair-category.can-list-assigned-to-me") },
    { key: "canListCollectiveRequests", label: t("model.permission.repair-category.can-list-collective") },
    { key: "canUpdateRequestStatus", label: t("model.permission.repair-category.can-update-status") },
  ];

  const isTree = props.ticketCategory?.type === "tree";
  const title = isEditMode
    ? t("page.repair-category.create-or-edit.edit-mode.title")
    : t("page.repair-category.create-or-edit.create-mode.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.repair-category.list.title"),
              to: routes.ticketCategories.list({ slug }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={props.onSubmit} data-testid="repair-category-form">
        <Form.Container>
          <Wizard
            actionsText={{
              finish: isEditMode
                ? t("component.wizard.action.edit.finish")
                : t("component.wizard.action.create.finish"),
            }}
            isSubmitting={props.isSubmitting}
            onFinish={formMethods.handleSubmit(props.onSubmit)}
            strictOrder={!isEditMode}
          >
            <WizardStep
              id={TABS_ID.STEP1}
              hasFinish={isEditMode}
              canLeave={commonWizardActionValidation}
              title={t("component.wizard.step-general")}
            >
              <Form.Body data-testid="repair-category-step-1">
                <FormField label={t("model.repair-category.name")} required>
                  <FormInput<TicketCategoryDetailsFormValues, "name">
                    data-testid="repair-category-name"
                    name="name"
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "model.repair-category.name"),
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", {
                          length: NAME_MAX_LENGTH,
                        }),
                        value: NAME_MAX_LENGTH,
                      },
                    }}
                    placeholder={t("model.repair-category.name.placeholder")}
                  />
                </FormField>
                <FormField label={t("model.repair-category.assignee")}>
                  <FormSelect<TicketCategoryDetailsFormValues, UserDto>
                    data-testid="repair-category-default-assignee"
                    name="defaultAssignee"
                    placeholder={t("model.repair-category.assignee.placeholder")}
                    items={possibleAssignees}
                    keySelector={(user) => user.id}
                    renderOption={(user) => user.fullName}
                    emptyItem={t("model.repair-category.assignee.no-assignee")}
                  />
                </FormField>
                {(!props.ticketCategory?.type || props.ticketCategory.type === "single") && (
                  <FormField label={t("model.repair-category.notification-type")}>
                    <FormErrorWrapper<TicketCategoryDetailsFormValues> name="notificationType">
                      <FormRadioCardGroup>
                        <FormRadioCard
                          data-testid="repair-category-notification-type-collective"
                          name="notificationType"
                          value="canBeCollective"
                          label={t("model.repair-category.notification-type.collective")}
                          description={t("model.repair-category.notification-type.collective.description")}
                        />
                        <FormRadioCard
                          data-testid="repair-category-notification-type-private"
                          name="notificationType"
                          value="alwaysPrivate"
                          label={t("model.repair-category.notification-type.private")}
                          description={t("model.repair-category.notification-type.private.description")}
                        />
                      </FormRadioCardGroup>
                    </FormErrorWrapper>
                  </FormField>
                )}
                <FormField
                  label={t("common.entity.audience")}
                  description={t("component.audience-section.description", {
                    audienceTarget: t("common.entity.ticket-category").toLowerCase(),
                  })}
                >
                  <AudienceSelector
                    defaultAudience={audience}
                    isEditMode={props.isEditMode}
                    onSave={(newAudience) => formMethods.setValue("audience", newAudience)}
                  />
                </FormField>
                <FormField
                  label={t("component.permission-section.title")}
                  description={t("component.permission-section.description", {
                    permissionTarget: t("common.entity.ticket-category").toLowerCase(),
                  })}
                >
                  <PermissionSelector
                    defaultPermissions={permissions}
                    fields={permissionFields}
                    onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
                  />
                </FormField>
              </Form.Body>
            </WizardStep>
            <WizardStep
              id={TABS_ID.STEP2}
              title={t("model.repair-category.step-tree")}
              hasFinish={isEditMode}
              canLeave={commonWizardActionValidation}
              isHidden={!(isTree && !!props.ticketCategory?.id)}
            >
              <Form.Body>
                <Suspense fallback={<FullSizeLoader withPadding />}>
                  {props.ticketCategory && (
                    <TicketCategoryTree
                      ticketCategory={props.ticketCategory}
                      isTreeVveEnabled={treeVveEnabled}
                      treeOverrideExternalCode={treeOverrideExternalCode}
                      onToggleTreeVve={(state) => formMethods.setValue("treeVveEnabled", state)}
                      onChangeTreeOverrideExternalCode={(code) =>
                        formMethods.setValue("treeOverrideExternalCode", code)
                      }
                    />
                  )}
                </Suspense>
              </Form.Body>
            </WizardStep>
            <WizardStep
              id={isTree ? TABS_ID.STEP3 : TABS_ID.STEP2}
              title={t("component.wizard.step-other")}
              hasFinish={isEditMode}
              canLeave={commonWizardActionValidation}
            >
              <Form.Body>
                <FormField label={t("model.repair-category.activity")}>
                  <FormErrorWrapper<TicketCategoryDetailsFormValues> name="closeAfterInactiveDayAmount">
                    <FormSelect<
                      TicketCategoryDetailsFormValues,
                      TicketCategoryDetailsFormValues["closeAfterInactiveDayAmount"] | "0"
                    >
                      keySelector={(x) => x}
                      items={[
                        "0",
                        ...DAYS_VARIANTS,
                        ...(!DAYS_VARIANTS.includes(closeAfterInactiveDayAmount as (typeof DAYS_VARIANTS)[number]) &&
                        closeAfterInactiveDayAmount !== "0"
                          ? [closeAfterInactiveDayAmount]
                          : []),
                      ]}
                      defaultValue={closeAfterInactiveDayAmount}
                      name="closeAfterInactiveDayAmount"
                      renderOption={(option) => {
                        switch (option) {
                          case "0":
                            return t("model.repair-category.activity.infinite");
                          default:
                            return t("model.repair-category.activity.days", { days: option });
                        }
                      }}
                      data-testid="repair-category-close-after-inactive-amount-select"
                    />
                  </FormErrorWrapper>
                </FormField>
                <FormField label={t("model.repair-category.reopen-policy")}>
                  <FormErrorWrapper<TicketCategoryDetailsFormValues> name="reopenPolicy">
                    <FormRadioGroup layout="vertical">
                      <FormRadio<TicketCategoryDetailsFormValues, "reopenPolicy">
                        data-testid="repair-category-reopen-policy-unlimited"
                        name="reopenPolicy"
                        value="unlimited"
                        label={t("model.repair-category.reopen-policy.unlimited")}
                      />
                      <FormRadio<TicketCategoryDetailsFormValues, "reopenPolicy">
                        data-testid="repair-category-reopen-policy-one-within-fourteen-days"
                        name="reopenPolicy"
                        value="oneWithinFourteenDays"
                        label={t("model.repair-category.reopen-policy.1-within-14-days")}
                      />
                      <FormRadio<TicketCategoryDetailsFormValues, "reopenPolicy">
                        data-testid="repair-category-reopen-policy-never"
                        name="reopenPolicy"
                        value="never"
                        label={t("model.repair-category.reopen-policy.never")}
                      />
                    </FormRadioGroup>
                  </FormErrorWrapper>
                </FormField>
                <FormField label={t("model.repair-category.phone-number-requirement")}>
                  <FormErrorWrapper<TicketCategoryDetailsFormValues> name="requirePhoneNumber">
                    <FormRadioGroup layout="vertical">
                      <FormRadio<TicketCategoryDetailsFormValues, "requirePhoneNumber">
                        data-testid="repair-category-require-phone-number-true"
                        name="requirePhoneNumber"
                        value="mandatory"
                        label={t("model.repair-category.phone-number-requirement.true")}
                      />
                      <FormRadio<TicketCategoryDetailsFormValues, "requirePhoneNumber">
                        data-testid="repair-category-require-phone-number-false"
                        name="requirePhoneNumber"
                        value="notRequired"
                        label={t("model.repair-category.phone-number-requirement.false")}
                      />
                    </FormRadioGroup>
                  </FormErrorWrapper>
                </FormField>
                <FormField label={t("model.repair-category.image-attachment-requirement")}>
                  <FormErrorWrapper<TicketCategoryDetailsFormValues> name="requireImages">
                    <FormRadioGroup layout="vertical">
                      <FormRadio<TicketCategoryDetailsFormValues, "requireImages">
                        data-testid="repair-category-require-images-true"
                        name="requireImages"
                        value="mandatory"
                        label={t("model.repair-category.image-attachment-requirement.true")}
                      />
                      <FormRadio<TicketCategoryDetailsFormValues, "requireImages">
                        data-testid="repair-category-require-images-false"
                        name="requireImages"
                        value="notRequired"
                        label={t("model.repair-category.image-attachment-requirement.false")}
                      />
                    </FormRadioGroup>
                  </FormErrorWrapper>
                </FormField>
                <FormField label={t("model.repair-category.is-active")}>
                  <FormErrorWrapper<TicketCategoryDetailsFormValues> name="enabled">
                    <FormSwitch<TicketCategoryDetailsFormValues, "enabled">
                      data-testid="repair-category-disabled-switch"
                      name="enabled"
                    />
                  </FormErrorWrapper>
                </FormField>
              </Form.Body>
            </WizardStep>
          </Wizard>
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
